/*  src/pages/StudentProfilePage.tsx  */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Box,
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';
import { useHasCompany } from '../hooks/useHasCompany.ts';

const StudentProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const hasCompany = useHasCompany();
  // display-only
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [yearLabel, setYearLabel] = useState<string>('—');

  // display-only
  const [email, setEmail] = useState('');

  // ui
  const [loading, setLoading] = useState(true);
  const [sendingReset, setSendingReset] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      setMsg(null);

      const {
        data: { user },
        error: uErr,
      } = await supabase.auth.getUser();
      if (uErr || !user) {
        setError('Not authenticated.');
        setLoading(false);
        return;
      }
      setEmail(user.email ?? '');

      const { data: stu, error: sErr } = await supabase
        .from('students')
        .select('first_name, last_name, year_id')
        .eq('user_id', user.id)
        .single();
      if (sErr) {
        setError(sErr.message);
        setLoading(false);
        return;
      }

      setFirst(stu?.first_name ?? '');
      setLast(stu?.last_name ?? '');

      if (stu?.year_id) {
        const { data: y } = await supabase
          .from('years')
          .select('year')
          .eq('id', stu.year_id)
          .single();
        if (y) setYearLabel(String(y.year));
      }

      setLoading(false);
    })();
  }, []);

  const handleSendReset = async () => {
    setMsg(null);
    setError(null);
    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSendingReset(false);
    error
      ? setError(error.message)
      : setMsg('Password reset email sent. Check your inbox.');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/practice', { replace: true });
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        My Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {msg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {msg}
        </Alert>
      )}

      {/* Basic info (read-only) */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: 'text.secondary' }}
        >
          Basic info
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              First name
            </Typography>
            <Typography variant="body1">{first || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Last name
            </Typography>
            <Typography variant="body1">{last || '—'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Year
            </Typography>
            <Typography variant="body1">{yearLabel}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Email (read-only) */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: 'text.secondary' }}
        >
          Email
        </Typography>
        <Typography variant="body1">{email || '—'}</Typography>
      </Paper>

      {/* Saved questions entry */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Saved questions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quickly revisit questions you've bookmarked while practising.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<BookmarkBorderIcon />}
            onClick={() => navigate('/practice/saved-questions')}
          >
            View saved questions
          </Button>
        </Stack>
      </Paper>

      {hasCompany === true && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                Mark disputes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See disputes you have sent to tutors after re-checking with AI.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<GavelOutlinedIcon />}
              onClick={() => navigate('/practice/mark-disputes')}
            >
              View mark disputes
            </Button>
          </Stack>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Bottom bar: reset on left, logout on right */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
      >
        <Button
          variant="outlined"
          onClick={handleSendReset}
          disabled={sendingReset}
        >
          {sendingReset ? 'Sending…' : 'Send reset password email'}
        </Button>

        <Button variant="outlined" color="error" onClick={handleLogout}>
          Log out
        </Button>
      </Stack>
    </Container>
  );
};

export default StudentProfilePage;
